import { extractApiErrorMessage, getApiBase, getAuthHeaders, clearAccessToken } from "./auth";

const API_BASE = getApiBase();
const ADMIN_ORG_CACHE_TTL_MS = 5 * 60 * 1000;

/** Canonical 4 roles (aligned with backend). */
export const ROLE_OPTIONS = ["owner", "admin", "sales", "ops"];

// Cache for CRM headers to avoid repeated getAdminOrgId calls
let cachedCrmHeaders: Record<string, string> | null = null;
let crmHeadersPromise: Promise<Record<string, string>> | null = null;

const crmHeaders = async () => {
  if (cachedCrmHeaders) {
    return cachedCrmHeaders;
  }

  if (crmHeadersPromise) {
    return crmHeadersPromise;
  }

  crmHeadersPromise = (async () => {
    try {
      const orgId = await getAdminOrgId();
      const headers = defaultHeaders(orgId);
      cachedCrmHeaders = headers;
      crmHeadersPromise = null;
      return headers;
    } catch (error: unknown) {
      crmHeadersPromise = null;
      throw error;
    }
  })();

  return crmHeadersPromise;
};

export async function listFarms(
  page: number = 1,
  pageSize: number = 100,
  filters?: {
    query?: string;
    status?: string;
    lifecycle?: string;
  }
) {
  const headers = await crmHeaders();
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  queryParams.append("page_size", pageSize.toString());
  if (filters?.query) queryParams.append("query", filters.query);
  if (filters?.status) queryParams.append("status", filters.status);
  if (filters?.lifecycle) queryParams.append("lifecycle", filters.lifecycle);
  const response = await fetch(`${API_BASE}/crm/farms?${queryParams.toString()}`, { headers });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to load clients";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function createFarm(payload: {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email?: string;
  contact_phone?: string;
  lifecycle_status?: string;
}) {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      organization: {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        address_line1: payload.address,
        city: payload.city,
        province: payload.province,
        postal_code: payload.postalCode,
        country: payload.country || "CA",
      },
      profile: {
        lifecycle_status: payload.lifecycle_status || "lead",
      },
      primary_contact: {
        contact_role: "owner",
        person: {
          first_name: payload.contact_first_name,
          last_name: payload.contact_last_name,
          email: payload.contact_email,
          phone: payload.contact_phone,
        },
      },
    }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to create client";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export type FarmDetail = {
  organization: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    province: string | null;
    postal_code: string | null;
    country: string | null;
    status: string;
  };
  profile: {
    lifecycle_status: string;
  };
  primary_contact: {
    id: string;
    farm_org_id: string;
    contact_role: string;
    is_primary: boolean;
    status: string;
    person: {
      id: string;
      first_name: string;
      last_name: string;
      email: string | null;
      phone: string | null;
    };
  } | null;
  contacts_count: number;
  notes_count: number;
  ambassador_name: string | null;
};

export async function getFarm(farmId: string): Promise<FarmDetail> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}`, {
    headers,
  });
  if (!response.ok) {
    throw new Error("Failed to load farm details");
  }
  return response.json();
}

/** CRM: Notes d'une ferme (org_notes). */
export type FarmNote = {
  id: string;
  org_id: string;
  note_type: string;
  content: string;
  visibility: string;
  created_by_user_id: string;
  created_in_org_id: string;
  created_at: string;
};

export async function listFarmNotes(
  farmId: string,
  params?: { type?: string; page?: number; page_size?: number }
): Promise<{ items: FarmNote[]; meta: { total: number; page: number; page_size: number } }> {
  const headers = await crmHeaders();
  const queryParams = new URLSearchParams();
  if (params?.type) queryParams.append("type", params.type);
  if (params?.page) queryParams.append("page", String(params.page));
  if (params?.page_size) queryParams.append("page_size", String(params.page_size ?? 100));
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}/notes?${queryParams.toString()}`, { headers });
  if (!response.ok) throw new Error("Failed to load notes");
  return response.json();
}

export async function addFarmNote(
  farmId: string,
  payload: { note_type: string; content: string; visibility?: string }
): Promise<FarmNote> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}/notes`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      note_type: payload.note_type,
      content: payload.content,
      visibility: payload.visibility ?? "xlkey_internal",
    }),
  });
  if (!response.ok) throw new Error("Failed to add note");
  return response.json();
}

/** CRM: Contact d'une ferme (farm_contact + person). */
export type FarmContact = {
  id: string;
  farm_org_id: string;
  contact_role: string;
  is_primary: boolean;
  status: string;
  person: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
  };
};

export async function listFarmContacts(farmId: string): Promise<FarmContact[]> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}/contacts`, { headers });
  if (!response.ok) throw new Error("Failed to load contacts");
  return response.json();
}

export async function addFarmContact(
  farmId: string,
  payload: {
    person: { first_name: string; last_name: string; email?: string; phone?: string };
    contact_role?: string;
    is_primary?: boolean;
  }
): Promise<FarmContact> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}/contacts`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      person: payload.person,
      contact_role: payload.contact_role ?? "other",
      is_primary: payload.is_primary ?? false,
      status: "active",
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    let msg = "Failed to add contact";
    try {
      const j = JSON.parse(text);
      if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
  return response.json();
}

export async function updateFarmContact(
  farmId: string,
  contactId: string,
  payload: {
    contact_role?: string;
    is_primary?: boolean;
    status?: string;
  }
): Promise<FarmContact> {
  const headers = await crmHeaders();
  const response = await fetch(
    `${API_BASE}/crm/farms/${farmId}/contacts/${contactId}`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    let msg = "Failed to update contact";
    try {
      const j = JSON.parse(text);
      if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
  return response.json();
}

export async function deleteFarmContact(
  farmId: string,
  contactId: string
): Promise<void> {
  const headers = await crmHeaders();
  const response = await fetch(
    `${API_BASE}/crm/farms/${farmId}/contacts/${contactId}`,
    { method: "DELETE", headers }
  );
  if (!response.ok) {
    const text = await response.text();
    let msg = "Failed to delete contact";
    try {
      const j = JSON.parse(text);
      if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
}

export async function updatePerson(
  personId: string,
  payload: {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
  }
): Promise<{ id: string; first_name: string; last_name: string; email: string | null; phone: string | null }> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/people/${personId}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    let msg = "Failed to update person";
    try {
      const j = JSON.parse(text);
      if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
  return response.json();
}

export async function updateFarm(
  farmId: string,
  payload: {
    organization?: {
      name?: string;
      email?: string;
      phone?: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      province?: string;
      postal_code?: string;
      country?: string;
    };
    profile?: {
      lifecycle_status?: string;
    };
  }
): Promise<FarmDetail> {
  const headers = await crmHeaders();
  const response = await fetch(`${API_BASE}/crm/farms/${farmId}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to update client";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

const defaultHeaders = (orgId?: string) => getAuthHeaders({ orgId, json: true });

let cachedAdminOrgId: string | null = null;
let cachedAdminOrgIdAt = 0;
let adminOrgIdPromise: Promise<string> | null = null;

/** Clear auth state and caches on 401; dispatch so App shows login. */
function handleUnauthorized() {
  clearAccessToken();
  cachedCrmHeaders = null;
  crmHeadersPromise = null;
  cachedAdminOrgId = null;
  cachedAdminOrgIdAt = 0;
  adminOrgIdPromise = null;
  window.dispatchEvent(new CustomEvent("xlflow:session-expired"));
}

export async function getAdminOrgId() {
  const now = Date.now();
  if (cachedAdminOrgId && now - cachedAdminOrgIdAt < ADMIN_ORG_CACHE_TTL_MS) {
    return cachedAdminOrgId;
  }

  if (adminOrgIdPromise) {
    return adminOrgIdPromise;
  }

  adminOrgIdPromise = (async () => {
    try {
      // First, try to get the user's memberships to find xlkey_internal org
      try {
        const meResponse = await fetch(`${API_BASE}/me`, {
          headers: getAuthHeaders(),
        });
        if (meResponse.status === 401) {
          adminOrgIdPromise = null;
          handleUnauthorized();
          throw new Error("Session expirée. Veuillez vous reconnecter.");
        }
        if (meResponse.ok) {
          const meData = await meResponse.json();
          const xlkeyMembership = meData.memberships?.find(
            (m: any) => m.organization?.type === "xlkey_internal" && m.status === "active"
          );
          if (xlkeyMembership?.organization_id) {
            cachedAdminOrgId = xlkeyMembership.organization_id;
            cachedAdminOrgIdAt = Date.now();
            adminOrgIdPromise = null;
            return xlkeyMembership.organization_id;
          }
        }
      } catch (meError: unknown) {
        if (meError instanceof Error && meError.message.includes("Session expirée")) {
          throw meError;
        }
        console.warn("Failed to get user memberships, falling back to direct org lookup:", meError);
      }

      // Fallback: Direct lookup (works for superadmins or if /me failed)
      const typeParam = encodeURIComponent("xlkey_internal");
      const response = await fetch(`${API_BASE}/admin/orgs?type=${typeParam}`, {
        headers: defaultHeaders(),
      });
      if (response.status === 401) {
        adminOrgIdPromise = null;
        handleUnauthorized();
        throw new Error("Session expirée. Veuillez vous reconnecter.");
      }
      if (!response.ok) {
        const errorText = await response.text();
        adminOrgIdPromise = null;
        throw new Error(`Failed to load admin org: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      const orgId = data.items?.[0]?.id;
      if (!orgId) {
        adminOrgIdPromise = null;
        throw new Error("Admin org not found");
      }
      cachedAdminOrgId = orgId;
      cachedAdminOrgIdAt = Date.now();
      adminOrgIdPromise = null;
      return orgId;
    } catch (error: unknown) {
      adminOrgIdPromise = null;
      throw error;
    }
  })();

  return adminOrgIdPromise;
}

export async function listAmbassadorOrgs(params?: { query?: string; status?: string; page?: number; page_size?: number }) {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.query) queryParams.append("query", params.query);
  if (params?.status) queryParams.append("status", params.status);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
  const response = await fetch(`${API_BASE}/admin/ambassadors?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }
  if (!response.ok) {
    throw new Error("Failed to load ambassadors");
  }
  return response.json();
}

export async function createAmbassadorOrg(payload: {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/ambassadors`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      address: payload.address,
      city: payload.city,
      province: payload.province,
      postal_code: payload.postal_code,
      country: payload.country || "CA",
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to create ambassador");
  }
  return response.json();
}

export async function addUserToAmbassador(ambassadorOrgId: string, userId: string, role: string) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/orgs/${ambassadorOrgId}/memberships`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({
      organization_id: ambassadorOrgId,
      user_id: userId,
      org_role: role,
      status: "active",
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to add user to ambassador");
  }
  return response.json();
}

export async function listAdminUsers(params?: { query?: string; status?: string; page?: number; page_size?: number }) {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.query) queryParams.append("query", params.query);
  if (params?.status) queryParams.append("status", params.status);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/admin/users?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load users");
  }
  return response.json();
}

export async function listUserMemberships(userId: string) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/users/${userId}/memberships`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load memberships");
  }
  return response.json();
}

export async function listOrganizations(params?: { query?: string; type?: string; status?: string; page?: number; page_size?: number }) {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.query) queryParams.append("query", params.query);
  if (params?.type) queryParams.append("type", params.type);
  if (params?.status) queryParams.append("status", params.status);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
  const response = await fetch(`${API_BASE}/admin/orgs?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (response.status === 401) {
    handleUnauthorized();
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }
  if (!response.ok) {
    throw new Error("Failed to load organizations");
  }
  return response.json();
}

export async function createOrganization(payload: {
  name: string;
  type: string;
  status?: string;
  email?: string;
  phone?: string;
  city?: string;
  province?: string;
}) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/orgs`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({
      name: payload.name,
      type: payload.type,
      status: payload.status || "active",
      email: payload.email,
      phone: payload.phone,
      city: payload.city,
      province: payload.province,
      country: "CA",
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to create organization");
  }
  return response.json();
}

export async function getOrg(orgId: string) {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/orgs/${orgId}`, {
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load organization");
  }
  return response.json();
}

export async function updateOrg(orgId: string, payload: {
  name?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
  status?: string;
}) {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/orgs/${orgId}`, {
    method: "PATCH",
    headers: defaultHeaders(adminOrgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update organization");
  }
  return response.json();
}

export async function updateOrganization(orgId: string, payload: { name?: string; status?: string }) {
  return updateOrg(orgId, payload);
}

export async function listOrgMemberships(orgId: string) {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/orgs/${orgId}/memberships`, {
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load org memberships");
  }
  return response.json();
}

export async function createAdminUser(payload: {
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  phone?: string;
  status?: "active" | "suspended";
  is_superadmin?: boolean;
  role: string;
  ambassador_org_id?: string;
  ambassador_role?: string;
}) {
  const orgId = await getAdminOrgId();
  const memberships = [
    {
      organization_id: orgId,
      org_role: payload.role,
      status: "active",
    },
  ];
  if (payload.ambassador_org_id) {
    memberships.push({
      organization_id: payload.ambassador_org_id,
      org_role: payload.ambassador_role || "ops",
      status: "active",
    });
  }
  const response = await fetch(`${API_BASE}/admin/users`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({
      email: payload.email,
      first_name: payload.first_name,
      last_name: payload.last_name,
      display_name: payload.display_name,
      phone: payload.phone,
      status: payload.status || "active",
      is_superadmin: payload.is_superadmin || false,
      memberships,
      send_invite: true, // Always send invitation when creating a new user
    }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to create user";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function updateAdminUser(userId: string, payload: { status?: "active" | "suspended" }) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update user");
  }
  return response.json();
}

export async function inviteAdminUser(userId: string) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/users/${userId}/invite`, {
    method: "POST",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to invite user"));
  }
  return response.json() as Promise<{ status: string }>;
}

export async function deleteAdminUser(userId: string) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to delete user"));
  }
  return;
}

export async function updateAdminUserDetails(
  userId: string,
  payload: { 
    email?: string; 
    first_name?: string; 
    last_name?: string; 
    display_name?: string; 
    phone?: string; 
    is_superadmin?: boolean;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    province?: string | null;
    postal_code?: string | null;
    country?: string | null;
  }
) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update user");
  }
  return response.json();
}

export async function updateMembership(
  membershipId: string,
  payload: { 
    org_role?: string | null; 
    custom_role_id?: string | null;
    status?: string; 
    permissions_override?: { allow?: string[]; deny?: string[] } | null 
  }
) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/memberships/${membershipId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update membership");
  }
  return response.json();
}

export async function createMembership(payload: {
  organization_id: string;
  user_id: string;
  org_role: string;
  status?: string;
}) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/memberships`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to create membership");
  }
  return response.json();
}

export async function disableMembership(membershipId: string) {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/memberships/${membershipId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to disable membership");
  }
  return response.json();
}

export async function updateAmbassadorOrg(
  orgId: string,
  payload: { name?: string; email?: string; phone?: string; city?: string; province?: string; status?: string }
) {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/ambassadors/${orgId}`, {
    method: "PATCH",
    headers: defaultHeaders(adminOrgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update ambassador");
  }
  return response.json();
}

export type AmbassadorStats = {
  total_client_farms: number;
  total_revenue: number;
  total_quotes: number;
  total_invoices: number;
  credits_purchased: number;
  credits_consumed: number;
  total_ops_jobs: number;
  active_ops_jobs: number;
};

export type AmbassadorDashboard = {
  organization: {
    id: string;
    name: string;
    type: string;
    status: string;
    email?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    country?: string;
    created_at?: string;
  };
  stats: AmbassadorStats;
  primary_contact?: {
    id: string;
    organization_id: string;
    org_role: string;
    status: string;
    user?: {
      id: string;
      email: string;
      first_name?: string;
      last_name?: string;
      display_name?: string;
    };
  };
};

export async function getAmbassadorDashboard(orgId: string): Promise<AmbassadorDashboard> {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/ambassadors/${orgId}/dashboard`, {
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load ambassador dashboard");
  }
  return response.json();
}

export async function getAmbassadorClients(
  ambassadorId: string,
  params?: { page?: number; page_size?: number }
) {
  const adminOrgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
  const response = await fetch(
    `${API_BASE}/admin/ambassadors/${ambassadorId}/clients?${queryParams.toString()}`,
    {
      headers: defaultHeaders(adminOrgId),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to load ambassador clients");
  }
  return response.json();
}

export async function getAmbassadorPurchases(
  ambassadorId: string,
  params?: { page?: number; page_size?: number }
) {
  const adminOrgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
  const response = await fetch(
    `${API_BASE}/admin/ambassadors/${ambassadorId}/purchases?${queryParams.toString()}`,
    {
      headers: defaultHeaders(adminOrgId),
    }
  );
  if (!response.ok) {
    throw new Error("Failed to load ambassador purchases");
  }
  return response.json();
}

export async function getAmbassadorManagement(ambassadorId: string) {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/ambassadors/${ambassadorId}/management`, {
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load ambassador management data");
  }
  return response.json();
}

export type AuditLog = {
  id: string;
  created_at: string;
  action: string;
  object_type: string;
  object_id: string | null;
  user_id: string | null;
  organization_id: string | null;
  before: Record<string, any> | null;
  after: Record<string, any> | null;
  request_id: string;
  message: string | null;
  is_superadmin_action: boolean | null;
};

export type PagedAuditLogs = {
  items: AuditLog[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  };
};

export async function listAuditLogs(params?: {
  object_type?: string;
  object_id?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedAuditLogs> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.object_type) queryParams.append("object_type", params.object_type);
  if (params?.object_id) queryParams.append("object_id", params.object_id);
  if (params?.from) queryParams.append("from", params.from);
  if (params?.to) queryParams.append("to", params.to);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());

  const response = await fetch(`${API_BASE}/admin/audit?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to load audit logs"));
  }
  return response.json();
}

// ============================================================================
// Custom Roles API
// ============================================================================

export type CustomRole = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  base_role: string | null;
  permissions: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CustomRoleCreate = {
  name: string;
  description?: string | null;
  base_role?: string | null;
  permissions: Record<string, any>;
};

export type CustomRoleUpdate = {
  name?: string;
  description?: string | null;
  permissions?: Record<string, any>;
  is_active?: boolean;
};

export async function listCustomRoles(organizationId: string): Promise<CustomRole[]> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/organizations/${organizationId}/custom-roles`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to load custom roles"));
  }
  return response.json();
}

export async function createCustomRole(
  organizationId: string,
  payload: CustomRoleCreate
): Promise<CustomRole> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/organizations/${organizationId}/custom-roles`, {
    method: "POST",
    headers: {
      ...defaultHeaders(orgId),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to create custom role"));
  }
  return response.json();
}

export async function getCustomRole(roleId: string): Promise<CustomRole> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/custom-roles/${roleId}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to load custom role"));
  }
  return response.json();
}

export async function updateCustomRole(
  roleId: string,
  payload: CustomRoleUpdate
): Promise<CustomRole> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/custom-roles/${roleId}`, {
    method: "PATCH",
    headers: {
      ...defaultHeaders(orgId),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to update custom role"));
  }
  return response.json();
}

export async function deleteCustomRole(roleId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/admin/custom-roles/${roleId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to delete custom role"));
  }
}
